import React, { useState, useEffect } from 'react';

const logoLight = `${process.env.PUBLIC_URL}/assets/clashroom-logo-light.png`;
const logoDark = `${process.env.PUBLIC_URL}/assets/clashroom-logo-dark.png`;

function useDarkMode() {
  const [isDark, setIsDark] = useState(
    () =>
      localStorage.getItem('theme') === 'dark' ||
      (window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches)
  );
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);
  return [isDark, setIsDark];
}

function ThemeToggle() {
  const [isDark, setIsDark] = useDarkMode();
  return (
    <button
      onClick={() => setIsDark(d => !d)}
      className="fixed right-4 top-4 z-50 rounded-full border border-clashGreen/70 bg-slate-900/90 px-3 py-1.5 text-xs font-medium text-slate-50 shadow-lg shadow-clashGreen/40 backdrop-blur dark:bg-zinc-900/90"
      aria-label="Toggle dark mode"
    >
      {isDark ? '☀️ Light' : '🌙 Dark'}
    </button>
  );
}

const SHARE_URL = 'https://clashroom.app/';
function ShareButton() {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Join the ClashRoom Waitlist!',
          url: SHARE_URL,
        });
      } else {
        await navigator.clipboard.writeText(SHARE_URL);
        setCopied(true);
        setTimeout(() => setCopied(false), 1300);
      }
    } catch {
      setCopied(false);
      alert('Could not share. Please copy the link: ' + SHARE_URL);
    }
  };
  return (
    <button
      onClick={share}
      type="button"
      className="mt-3 inline-flex items-center justify-center rounded-full bg-clashGreen px-4 py-2 text-xs font-semibold text-slate-950 shadow-md shadow-clashGreen/40 transition hover:-translate-y-0.5 hover:bg-clashBlue"
    >
      {copied ? 'Copied!' : 'Share this link'}
    </button>
  );
}

const stats = [
  { label: 'Fact-checks run', value: '24,981' },
  { label: 'Clash clips watched', value: '312K+' },
  { label: 'Avg watch time', value: '7.4 min' },
];

const testimonials = [
  {
    quote:
      'Came here cocky, left with a list of things to Google. This app’s a humbler.',
    author: 'Jaylen (Brooklyn, NY)',
    emoji: '😂',
  },
  {
    quote:
      'Tried to run my mouth—got fact-checked by a 19-year-old. I respect it.',
    author: 'Liz (Miami, FL)',
    emoji: '🥲',
  },
  {
    quote:
      'Way better than group chats, ‘cause you actually learn something.',
    author: 'Manny (Houston, TX)',
    emoji: '📲',
  },
  {
    quote: 'Finally, an app where trolling makes you lose points. Shook.',
    author: 'Aliyah (Seattle, WA)',
    emoji: '🤓',
  },
  {
    quote:
      "No more ‘my opinion is my fact.’ If you can't back it up, you're getting roasted.",
    author: 'Kevin (Chicago, IL)',
    emoji: '🫡',
  },
  {
    quote:
      'Debated my cousin, lost in public, and somehow I’m still coming back. Wild.',
    author: 'Jess (Los Angeles, CA)',
    emoji: '👀',
  },
];

const featureCards = [
  {
    title: 'TikTok-style Clash feed',
    body: 'Swipe through short debates, hot takes, and breakdowns instead of random dances and pranks.',
    icon: '🎥',
  },
  {
    title: 'Live AI + crowd fact-checks',
    body: 'See receipts, sources, and community verdicts layered on top of the video in real time.',
    icon: '🧾',
  },
  {
    title: 'ClashCred reputation',
    body: 'Earn credibility when your takes age well and your receipts check out. Lose it when you bluff.',
    icon: '🏆',
  },
  {
    title: 'Non-debater mode',
    body: 'Watch clips, vote on claims, help flag BS, and still build Cred without ever taking the mic.',
    icon: '👀',
  },
  {
    title: 'Creator-ready overlays',
    body: 'Clash overlays travel with your clips so your audience can see the context on any platform.',
    icon: '⚡',
  },
  {
    title: 'Troll filter built-in',
    body: 'The more you troll, the less the system listens. Smart behavior is rewarded, spam is buried.',
    icon: '🚫',
  },
];

const howItWorks = [
  'Pick a topic or join a live clash from the feed.',
  'Drop your take or reaction and attach your receipts.',
  'ClashBot, the crowd, and creators fact-check in real time.',
  'The clip locks in with a visible truth + context track.',
];

const faqs = [
  {
    q: 'What is ClashRoom?',
    a: 'ClashRoom is a short-form clash feed where every take is fact-checked by AI and the crowd in real time. You see context and receipts on the clip, not buried in the comments.',
  },
  {
    q: 'Is this only for politics?',
    a: 'No. You can clash on sports, pop culture, podcasts, policy, and everything in between. If there are receipts, ClashRoom can surface them.',
  },
  {
    q: 'Do I have to debate?',
    a: 'No. You can stay in non-debater mode: watch clips, vote, react, and help fact-check. The app treats smart viewers as part of the system.',
  },
  {
    q: 'How is this different from TikTok or Reddit?',
    a: 'TikTok and Reddit are built for engagement first. ClashRoom is built for receipts first: clips are designed to show sources, credibility, and context without leaving the video.',
  },
  {
    q: 'How do I earn Cred?',
    a: 'Bring facts, bring receipts, and let the crowd (and ClashBot) judge. Every correct claim, vote, or fact-check earns you ClashCred™.',
  },
  {
    q: 'Can I lose Cred?',
    a: 'Yes. If you spam, troll, or repeatedly push claims that don’t check out, your Cred drops and the system takes you less seriously.',
  },
];

function useClashCounter(start = 18419) {
  const [count, setCount] = useState(start);
  useEffect(() => {
    const interval = setInterval(
      () => setCount(c => c + Math.floor(Math.random() * 3)),
      4000
    );
    return () => clearInterval(interval);
  }, []);
  return count;
}

const ClashRoomLanding = () => {
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(false);
  const clashCounter = useClashCounter();

  useEffect(() => {
    const interval = setInterval(
      () => setTestimonialIndex(prev => (prev + 1) % testimonials.length),
      6000
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  }, [email]);

  const handleScrollToWaitlist = e => {
    e.preventDefault();
    const el = document.getElementById('waitlist');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = e => {
    setTimeout(() => {
      window.location.href = '/thankyou';
    }, 250);
  };

  const currentTestimonial = testimonials[testimonialIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 text-slate-50 dark:from-black dark:via-black dark:to-black">
      {/* brand color wash */}
      <div className="pointer-events-none fixed inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.24),transparent_60%),radial-gradient(circle_at_20%_80%,_rgba(52,211,153,0.22),transparent_55%)] blur-3xl" />

      <ThemeToggle />

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col px-4 pb-16 pt-6 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="rounded-2xl bg-slate-950/90 p-1.5 shadow-lg shadow-clashGreen/40">
              <img
                src={logoLight}
                alt="ClashRoom logo"
                className="h-8 w-8 rounded-xl dark:hidden"
                draggable="false"
              />
              <img
                src={logoDark}
                alt="ClashRoom logo dark"
                className="hidden h-8 w-8 rounded-xl dark:block"
                draggable="false"
              />
            </div>
            <span className="text-lg font-semibold tracking-tight text-white">
              ClashRoom
            </span>
          </div>

          <nav className="hidden items-center gap-6 text-xs font-medium text-slate-300 sm:flex">
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById('features');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="hover:text-clashGreen"
            >
              Features
            </button>
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById('how-it-works');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="hover:text-clashGreen"
            >
              How it works
            </button>
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById('faq');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="hover:text-clashGreen"
            >
              FAQ
            </button>
            <a
              href="#waitlist"
              onClick={handleScrollToWaitlist}
              className="rounded-full border border-clashGreen/70 bg-clashGreen/10 px-4 py-1.5 text-[11px] font-semibold tracking-wide text-clashGreen shadow-sm shadow-clashGreen/30 backdrop-blur transition hover:bg-clashGreen/20"
            >
              Get early access
            </a>
          </nav>
        </header>

        {/* Hero */}
        <main className="mt-10 flex flex-1 flex-col gap-16 lg:mt-16 lg:flex-row lg:items-center">
          <section className="relative z-10 max-w-xl space-y-6">
            <p className="inline-flex items-center gap-2 rounded-full border border-clashGreen/40 bg-slate-900/80 px-3 py-1 text-[11px] font-medium text-slate-200 shadow-sm backdrop-blur">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-clashGreen/20 text-[10px] text-clashGreen">
                ⚡
              </span>
              Human-first, AI-powered clash feed
            </p>

            <h1 className="text-balance text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              TikTok-style debates
              <span className="block bg-gradient-to-r from-clashGreen via-clashBlue to-sky-400 bg-clip-text text-transparent">
                with receipts instead of vibes.
              </span>
            </h1>

            <p className="text-pretty text-sm text-slate-200 sm:text-base">
              ClashRoom is a short-form clash feed where every take is
              fact-checked by AI and the crowd in real time. Watch creators
              battle it out, see receipts over the clip, and help decide what
              actually holds up.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="#waitlist"
                onClick={handleScrollToWaitlist}
                className="inline-flex items-center justify-center rounded-xl bg-clashGreen px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-clashGreen/40 transition hover:-translate-y-0.5 hover:bg-clashBlue"
              >
                Join the early access list
              </a>
              <a
                href="#clips"
                className="inline-flex items-center justify-center gap-2 text-[11px] font-medium text-slate-300 hover:text-clashGreen"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-700 bg-slate-950/80 text-[10px] shadow-sm">
                  ▶
                </span>
                Watch example ClashClips
              </a>
            </div>

            <ul className="mt-4 grid gap-2 text-xs text-slate-200 sm:text-sm">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-clashGreen" />
                <span>Live fact-check overlays while the debate is happening.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-clashGreen" />
                <span>Non-debater mode so lurkers and learners still earn Cred.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-clashGreen" />
                <span>Cred score that rewards receipts, not just hot takes.</span>
              </li>
            </ul>

            <div className="mt-5 grid gap-4 rounded-2xl border border-slate-700 bg-slate-800/80 p-4 text-xs text-slate-200 sm:grid-cols-3">
              {stats.map(item => (
                <div key={item.label} className="space-y-1">
                  <div className="text-sm font-semibold text-white">
                    {item.value}
                  </div>
                  <div className="text-[11px] text-slate-400">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 text-[11px] text-slate-300">
              <span className="font-semibold text-clashGreen">
                {clashCounter.toLocaleString()}
              </span>{' '}
              verified facts processed in our early tests — and counting.
            </div>
          </section>

          {/* Phone preview */}
          <section
            className="relative mx-auto mt-4 flex w-full max-w-sm justify-center lg:mt-0"
            aria-label="ClashClips feed preview"
            id="clips"
          >
            <div className="relative h-[520px] w-[260px] rounded-[32px] border border-clashGreen/40 bg-gradient-to-b from-slate-900 via-slate-800 to-black p-3 shadow-[0_0_40px_rgba(52,211,153,0.4)]">
              <div className="mx-auto h-full w-full rounded-[26px] border border-slate-700 bg-slate-900/90 p-2">
                <div className="flex items-center justify-between px-1.5 pb-1.5 pt-0.5 text-[10px] text-slate-400">
                  <span className="rounded-full bg-slate-950 px-2 py-0.5 text-[9px] text-clashGreen">
                    Live clashes
                  </span>
                  <span>For you • Smart feed</span>
                </div>

                <div className="relative mt-1 flex h-[360px] flex-col overflow-hidden rounded-2xl bg-slate-800">
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/avatar4.png`}
                    alt="Creator avatar"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-60"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                  <div className="relative z-10 flex h-full flex-col justify-between p-3 text-[10px] text-slate-200">
                    <div className="space-y-2">
                      <div className="inline-flex items-center gap-2 rounded-full bg-black/70 px-2 py-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-clashGreen" />
                        <span className="text-[9px] font-semibold tracking-wide text-clashGreen">
                          Claim under review
                        </span>
                        <span className="text-[8px] text-slate-400">
                          7 sources checking
                        </span>
                      </div>
                      <p className="text-[11px] font-medium">
                        “No rookie QB has ever taken a team this far in year one.”
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <img
                            src={`${process.env.PUBLIC_URL}/assets/avatar2.png`}
                            alt="Creator"
                            className="h-6 w-6 rounded-full border border-slate-700"
                          />
                          <div>
                            <div className="text-[11px] font-medium">
                              @hotseat_sports
                            </div>
                            <div className="text-[9px] text-slate-400">
                              Clash vs @numbersdontlie
                            </div>
                          </div>
                        </div>
                        <div className="rounded-full bg-black/70 px-2 py-1 text-[9px] text-clashGreen">
                          ClashCred 87
                        </div>
                      </div>

                      <div className="space-y-1 rounded-xl bg-black/70 p-2">
                        <div className="flex items-center justify-between text-[9px] text-slate-300">
                          <span>ClashBot verdict (live)</span>
                          <span className="rounded-full bg-clashGreen/10 px-2 py-0.5 text-[9px] text-clashGreen">
                            Context needed
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                          <div
                            className="h-full bg-gradient-to-r from-clashGreen via-yellow-300 to-red-400"
                            style={{ width: '68%' }}
                          />
                        </div>
                        <div className="flex justify-between text-[9px] text-slate-400">
                          <span>Receipts: 5 stats, 2 articles</span>
                          <span>2:34 remaining</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[9px] text-slate-300">
                        <div className="flex gap-2">
                          <span className="rounded-full bg-slate-900/90 px-2 py-1">
                            👍 2.1K
                          </span>
                          <span className="rounded-full bg-slate-900/90 px-2 py-1">
                            🧾 Fact-check
                          </span>
                          <span className="rounded-full bg-slate-900/90 px-2 py-1">
                            💬 328
                          </span>
                        </div>
                        <span className="text-[9px] text-slate-400">
                          Swipe ↑ to ClashBack
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-2 grid grid-cols-3 gap-1.5 text-[9px] text-slate-200">
                  <div className="rounded-lg border border-slate-800 bg-slate-950/80 p-1.5">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[10px] font-semibold">Politics</span>
                      <span className="rounded-full bg-red-500/15 px-1.5 py-0.5 text-[8px] text-red-300">
                        Spicy
                      </span>
                    </div>
                    <p className="line-clamp-2">
                      Fact-checking viral clips from the latest debate night.
                    </p>
                  </div>
                  <div className="rounded-lg border border-slate-800 bg-slate-950/80 p-1.5">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[10px] font-semibold">
                        Pop culture
                      </span>
                      <span className="rounded-full bg-clashGreen/15 px-1.5 py-0.5 text-[8px] text-clashGreen">
                        New
                      </span>
                    </div>
                    <p className="line-clamp-2">
                      Who actually said what in that podcast everyone is clipping.
                    </p>
                  </div>
                  <div className="rounded-lg border border-slate-800 bg-slate-950/80 p-1.5">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[10px] font-semibold">Sports</span>
                      <span className="rounded-full bg-clashBlue/15 px-1.5 py-0.5 text-[8px] text-clashBlue">
                        Live
                      </span>
                    </div>
                    <p className="line-clamp-2">
                      Real-time receipt checks on hot takes during the game.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Features */}
        <section
          id="features"
          className="mt-16 space-y-6 rounded-3xl border border-slate-800 bg-slate-900 p-6 shadow-xl shadow-black/40 sm:p-8"
        >
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <h2 className="text-xl font-semibold text-white sm:text-2xl">
                Built for people who actually care if it&apos;s true.
              </h2>
              <p className="mt-2 max-w-xl text-sm text-slate-300">
                ClashRoom is part social app, part lie detector. Every feature is
                designed to make receipts and context more entertaining than
                misinformation.
              </p>
            </div>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {featureCards.map(card => (
              <div
                key={card.title}
                className="flex flex-col justify-between rounded-2xl border border-slate-800 bg-slate-900/90 p-4"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{card.icon}</span>
                  <h3 className="text-sm font-semibold text-white">
                    {card.title}
                  </h3>
                </div>
                <p className="mt-2 text-xs text-slate-300">{card.body}</p>
                <div className="mt-3 h-px w-10 bg-gradient-to-r from-clashGreen to-clashBlue" />
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section
          id="how-it-works"
          className="mt-14 grid gap-8 rounded-3xl border border-slate-800 bg-slate-900 p-6 shadow-xl shadow-black/40 sm:grid-cols-[1.1fr,0.9fr] sm:p-8"
        >
          <div>
            <h2 className="text-xl font-semibold text-white sm:text-2xl">
              How ClashRoom works
            </h2>
            <p className="mt-2 text-sm text-slate-300">
              Under the hood, ClashRoom blends a social video feed, an AI
              verification engine, and crowd-sourced context into one interface.
            </p>
            <ol className="mt-4 space-y-3 text-sm text-slate-200">
              {howItWorks.map((step, index) => (
                <li key={step} className="flex gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-clashGreen">
                    {index + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="space-y-4 rounded-2xl border border-clashGreen/40 bg-gradient-to-b from-clashGreen/10 via-slate-950 to-slate-950 p-4 text-xs text-slate-100">
            <div className="flex items-center gap-3">
              <img
                src={`${process.env.PUBLIC_URL}/assets/avatar6.png`}
                alt="User avatar"
                className="h-9 w-9 rounded-full border border-clashGreen/70"
              />
              <div>
                <div className="text-sm font-semibold">@factcheck_jules</div>
                <div className="text-[11px] text-clashGreen">
                  ClashRoom beta tester
                </div>
              </div>
            </div>
            <p>
              “I used to doom-scroll arguments on other apps and walk away
              annoyed and confused. On ClashRoom, I can actually see who&apos;s
              bluffing and who did their homework.”
            </p>
            <div className="rounded-xl border border-clashGreen/40 bg-black/60 p-3 text-[11px] text-clashGreen/90">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-clashGreen">
                Why it matters
              </div>
              <p>
                Debate doesn&apos;t have to be toxic or boring. With the right
                tools, it becomes a way to make people smarter without preaching
                at them.
              </p>
            </div>
          </div>
        </section>

        {/* Waitlist */}
        <section
          id="waitlist"
          className="mt-14 rounded-3xl border border-slate-800 bg-slate-900 p-6 shadow-xl shadow-black/40 sm:p-8"
        >
          <div className="grid gap-6 md:grid-cols-[1.1fr,0.9fr]">
            <div>
              <h2 className="text-xl font-semibold text-white sm:text-2xl">
                Be part of the first ClashRoom cohort.
              </h2>
              <p className="mt-2 text-sm text-slate-300">
                We&apos;re rolling out access in waves so the experience stays
                sharp and the fact-check layer stays strong. Drop your email and
                we&apos;ll invite you as we open new slots.
              </p>
              <p className="mt-2 text-xs text-slate-400">
                No spam, no drama. Just an invite when it&apos;s your turn.
              </p>
              <ShareButton />
            </div>

            <form
              action="https://hooks.zapier.com/hooks/catch/23692566/u3lqrp4/"
              method="POST"
              className="space-y-3"
              onSubmit={handleSubmit}
            >
              <label className="block text-xs font-medium text-slate-200">
                Email address
              </label>
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-3 py-2 text-sm text-slate-50 outline-none ring-clashGreen/40 focus:border-clashGreen focus:ring"
              />
              <button
                type="submit"
                disabled={!emailValid}
                className="w-full rounded-xl bg-clashGreen px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-md shadow-clashGreen/40 transition hover:-translate-y-0.5 hover:bg-clashBlue disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
              >
                Join the early access waitlist
              </button>
              <p className="text-[11px] text-slate-400">
                By joining, you agree to receive one-off product updates about
                ClashRoom. You can opt out anytime.
              </p>
            </form>
          </div>
        </section>

        {/* Testimonials */}
        <section className="mt-14 flex flex-col items-center px-2 text-center">
          <h2 className="text-lg font-semibold text-white sm:text-xl">
            What real people are saying already
          </h2>
          <div className="mt-4 w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-xl shadow-black/40">
            <blockquote className="text-pretty text-sm italic text-slate-100 sm:text-base">
              “{currentTestimonial.quote}”
            </blockquote>
            <div className="mt-3 text-xs text-slate-300">
              {currentTestimonial.emoji} {currentTestimonial.author}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mt-14">
          <h2 className="text-lg font-semibold text-white sm:text-xl">
            Frequently asked questions
          </h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {faqs.map(item => (
              <div
                key={item.q}
                className="rounded-2xl border border-slate-800 bg-slate-900 p-4 text-sm text-slate-200 shadow-lg shadow-black/40"
              >
                <div className="text-sm font-semibold text-white">
                  {item.q}
                </div>
                <p className="mt-2 text-xs text-slate-300">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-12 flex flex-col justify-between gap-3 border-t border-slate-800 pt-4 text-xs text-slate-400 sm:flex-row sm:items-center">
          <div>
            © {new Date().getFullYear()} ClashRoom. All rights reserved.
          </div>
          <div className="flex gap-3">
            <a href="/privacy" className="hover:text-clashGreen">
              Privacy
            </a>
            <a href="/terms" className="hover:text-clashGreen">
              Terms
            </a>
            <a
              href="mailto:hello@clashroom.app"
              className="hover:text-clashGreen"
            >
              Contact
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ClashRoomLanding;
